import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { AdminHeader, Panel, TextInput, TextArea, EmptyState } from "@/components/admin/ui";
import { useAuth } from "@/context/AuthContext";
import { canAccess } from "@/lib/perms";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

const CONFIG = {
  news: {
    title: "News & Market Analysis", subtitle: "Publish research and market commentary.",
    label: (i) => i.title, sub: (i) => i.category,
    fields: [
      { k: "title", label: "Title", type: "text", req: true },
      { k: "category", label: "Category", type: "text" },
      { k: "author", label: "Author", type: "text" },
      { k: "image", label: "Cover Image URL", type: "text" },
      { k: "excerpt", label: "Excerpt", type: "textarea" },
      { k: "body", label: "Body", type: "textarea", rows: 8 },
    ],
  },
  trading_ideas: {
    title: "Trading Ideas", subtitle: "Publish and schedule market-outlook posts for the homepage. Grouped by the Instrument tab.",
    label: (i) => i.title, sub: (i) => `${i.instrument || "General"}${i.date ? " · " + new Date(i.date).toLocaleDateString() : ""}`,
    fields: [
      { k: "title", label: "Title", type: "text", req: true },
      { k: "instrument", label: "Instrument / Tab (e.g. EUR/USD, Gold)", type: "text", req: true },
      { k: "bias", label: "Bias (Bullish / Bearish / Neutral)", type: "text" },
      { k: "date", label: "Idea Date", type: "date" },
      { k: "chart_image", label: "Chart Image URL", type: "text" },
      { k: "summary", label: "Summary (shown on card)", type: "textarea", rows: 3, req: true },
      { k: "body", label: "Full Write-up", type: "textarea", rows: 8 },
    ],
  },
  education: {
    title: "Education Center", subtitle: "Manage trading guides and learning content.",
    label: (i) => i.title, sub: (i) => `${i.category || ""} · ${i.level || ""}`,
    fields: [
      { k: "title", label: "Title", type: "text", req: true },
      { k: "category", label: "Category", type: "text" },
      { k: "level", label: "Level (Beginner/Intermediate/Advanced)", type: "text" },
      { k: "read_time", label: "Read time e.g. 6 min", type: "text" },
      { k: "excerpt", label: "Summary", type: "textarea" },
    ],
  },
  faq: {
    title: "FAQ Manager", subtitle: "Add and categorize frequently asked questions.",
    label: (i) => i.question, sub: (i) => i.category,
    fields: [
      { k: "question", label: "Question", type: "text", req: true },
      { k: "answer", label: "Answer", type: "textarea", rows: 4, req: true },
      { k: "category", label: "Category", type: "text" },
    ],
  },
  promotions: {
    title: "Promotions Manager", subtitle: "Manage time-bound offers and campaigns.",
    label: (i) => i.title, sub: (i) => i.end_date ? `Ends ${new Date(i.end_date).toLocaleDateString()}` : "No end date",
    fields: [
      { k: "title", label: "Title", type: "text", req: true },
      { k: "badge", label: "Badge e.g. Limited Time", type: "text" },
      { k: "description", label: "Description", type: "textarea", rows: 3 },
      { k: "link_key", label: "Link Key (from Link Manager) e.g. live_register", type: "text" },
      { k: "end_date", label: "End Date", type: "date" },
    ],
  },
  testimonials: {
    title: "Testimonials Manager", subtitle: "Add verified client testimonials. Only publish real, verified feedback.",
    label: (i) => i.author, sub: (i) => i.role,
    fields: [
      { k: "author", label: "Author Name", type: "text", req: true },
      { k: "role", label: "Role / Title", type: "text" },
      { k: "quote", label: "Quote", type: "textarea", rows: 3, req: true },
      { k: "avatar", label: "Avatar Image URL", type: "text" },
      { k: "rating", label: "Rating (1-5)", type: "number" },
    ],
  },
  hero_slides: {
    title: "Hero Slides", subtitle: "CMS-managed photographic slides shown behind the homepage headline.",
    label: (i) => i.title, sub: (i) => i.overline,
    fields: [
      { k: "overline", label: "Overline (small label)", type: "text" },
      { k: "title", label: "Headline", type: "text", req: true },
      { k: "subtitle", label: "Subtitle", type: "textarea", rows: 2 },
      { k: "image", label: "Background Image URL", type: "text" },
      { k: "primary_label", label: "Primary Button Label", type: "text" },
      { k: "primary_key", label: "Primary Button Link Key (e.g. live_register)", type: "text" },
      { k: "secondary_label", label: "Secondary Button Label", type: "text" },
      { k: "secondary_key", label: "Secondary Link Key (external) — optional", type: "text" },
      { k: "secondary_to", label: "Secondary Internal Path (e.g. /markets) — optional", type: "text" },
    ],
  },
  videos: {
    title: "Latest Videos", subtitle: "Paste a YouTube link/ID so the homepage video row plays your actual tutorials.",
    label: (i) => i.title, sub: (i) => (i.youtube_id ? `YouTube: ${i.youtube_id}` : "No video linked"),
    fields: [
      { k: "title", label: "Video Title", type: "text", req: true },
      { k: "youtube_id", label: "YouTube URL or Video ID", type: "text" },
    ],
  },
  events: {
    title: "Economic Calendar", subtitle: "Upcoming market events with impact levels, forecasts and previous readings.",
    label: (i) => i.title, sub: (i) => `${i.currency || ""} · ${i.impact || ""} · ${i.date ? new Date(i.date).toLocaleDateString() : ""}`,
    fields: [
      { k: "title", label: "Event", type: "text", req: true },
      { k: "currency", label: "Currency (USD, EUR, GBP...)", type: "text" },
      { k: "impact", label: "Impact (high / medium / low)", type: "text" },
      { k: "date", label: "Date & Time", type: "datetime-local" },
      { k: "forecast", label: "Forecast", type: "text" },
      { k: "previous", label: "Previous", type: "text" },
    ],
  },
};

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
};

export default function ContentManager({ resource }) {
  const cfg = CONFIG[resource];
  const { user } = useAuth();
  const canEdit = canAccess(user?.role, resource);
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [form, setForm] = useState({});
  const [sched, setSched] = useState("publish");

  const load = () => api.get(`/content/${resource}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [resource]);

  const openNew = () => { setForm({ published: true }); setSched("publish"); setEditing("new"); };
  const openEdit = (i) => {
    setForm({ ...i });
    setSched(i.scheduled_for ? "schedule" : (i.published === false ? "draft" : "publish"));
    setEditing(i.id);
  };

  const save = async () => {
    for (const f of cfg.fields) if (f.req && !form[f.k]) { toast.error(`${f.label} is required`); return; }
    let published = true;
    let scheduled_for = null;
    if (sched === "draft") { published = false; }
    else if (sched === "schedule") {
      if (!form.scheduled_for) { toast.error("Pick a date & time to schedule"); return; }
      const dt = new Date(form.scheduled_for);
      if (isNaN(dt)) { toast.error("Invalid schedule date"); return; }
      scheduled_for = dt.toISOString();
      published = false;
    }
    const data = { ...form, published, scheduled_for };
    try {
      if (editing === "new") await api.post(`/content/${resource}`, { data });
      else await api.put(`/content/${resource}/${editing}`, { data });
      toast.success(sched === "schedule" ? "Scheduled" : "Saved"); setEditing(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const togglePub = async (i) => {
    try { await api.put(`/content/${resource}/${i.id}`, { data: { ...i, published: !i.published, scheduled_for: null } }); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const del = async () => {
    try { await api.delete(`/content/${resource}/${confirm.id}`); toast.success("Deleted"); setConfirm(null); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <AdminHeader title={cfg.title} subtitle={cfg.subtitle}
        action={canEdit && <button onClick={openNew} className="btn-gold px-4 py-2.5 text-sm inline-flex items-center gap-2" data-testid="content-add-btn"><Plus size={16} /> Add New</button>} />

      {items.length === 0 ? <Panel><EmptyState>No items yet. Click “Add New” to create one.</EmptyState></Panel> : (
        <Panel>
          <div className="divide-y divide-[#203E70]">
            {items.map((i) => (
              <div key={i.id} className="flex items-center gap-4 p-4" data-testid={`content-row-${i.id}`}>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium truncate ${i.published !== false ? "text-white" : "text-slate-500"}`}>{cfg.label(i)}</p>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{cfg.sub(i)}</p>
                </div>
                {i.scheduled_for
                  ? <span className="text-xs text-sky-400 shrink-0" title={new Date(i.scheduled_for).toLocaleString()} data-testid={`content-scheduled-${i.id}`}>Scheduled · {new Date(i.scheduled_for).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                  : i.published === false && <span className="text-xs text-amber-500/80 shrink-0">Draft</span>}
                <div className="flex items-center gap-1 shrink-0">
                  {canEdit && <button onClick={() => togglePub(i)} className={`p-2 ${i.published !== false ? "text-[#D8AE4E]" : "text-slate-500"}`} title="Publish/Unpublish" data-testid={`content-pub-${i.id}`}>{i.published !== false ? <Eye size={16} /> : <EyeOff size={16} />}</button>}
                  {canEdit && <button onClick={() => openEdit(i)} className="p-2 text-slate-400 hover:text-[#D8AE4E]" data-testid={`content-edit-${i.id}`}><Pencil size={15} /></button>}
                  {canEdit && <button onClick={() => setConfirm(i)} className="p-2 text-slate-400 hover:text-red-400" data-testid={`content-delete-${i.id}`}><Trash2 size={15} /></button>}
                </div>
              </div>
            ))}
          </div>
        </Panel>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-[#0B1D40] border-[#203E70] text-white max-h-[85vh] overflow-y-auto admin-scroll">
          <DialogHeader><DialogTitle className="font-serif text-xl">{editing === "new" ? "Add" : "Edit"} {cfg.title}</DialogTitle><DialogDescription className="text-slate-400 text-sm">{cfg.subtitle}</DialogDescription></DialogHeader>
          <div className="space-y-4">
            {cfg.fields.map((f) => (
              f.type === "textarea"
                ? <TextArea key={f.k} label={f.label + (f.req ? " *" : "")} rows={f.rows || 3} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} testid={`field-${f.k}`} />
                : <TextInput key={f.k} label={f.label + (f.req ? " *" : "")} type={f.type} value={form[f.k] || ""} onChange={(e) => setForm({ ...form, [f.k]: e.target.value })} testid={`field-${f.k}`} />
            ))}
            <div className="space-y-3 pt-3 border-t border-[#203E70]">
              <p className="text-xs uppercase tracking-wide text-slate-400">Publishing</p>
              <div className="flex flex-wrap gap-2">
                {[["publish", "Publish now"], ["draft", "Save as draft"], ["schedule", "Schedule"]].map(([v, l]) => (
                  <button type="button" key={v} onClick={() => setSched(v)} className={`px-3 py-2 text-sm rounded border transition-colors ${sched === v ? "btn-gold border-transparent" : "border-[#203E70] text-slate-300 hover:border-[#D8AE4E]/60"}`} data-testid={`sched-${v}`}>{l}</button>
                ))}
              </div>
              {sched === "schedule" && (
                <TextInput label="Publish at" type="datetime-local" value={toLocalInput(form.scheduled_for)} onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} testid="field-scheduled-for" />
              )}
            </div>
          </div>
          <DialogFooter>
            <button type="button" onClick={() => setEditing(null)} className="btn-outline-gold px-4 py-2 text-sm">Cancel</button>
            <button type="button" onClick={save} className="btn-gold px-4 py-2 text-sm" data-testid="content-save">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-[#0B1D40] border-[#203E70] text-white">
          <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle><AlertDialogDescription className="text-slate-400">This action cannot be undone.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[#203E70] text-white hover:bg-[#102653]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={del} className="bg-red-600 hover:bg-red-700 text-white" data-testid="content-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
